/*
  The combineStrings function makes pretty string from an array of incoming params.
  The good example its make full address string from the params
    {
      "state": "il",
      "city": "Roanoke",
      "address1": "1408 W Front St",
      "address2": "",
      "zip": "61561",
    }
  If we will create string like concat string `${address1} ${address1}, ${city}, ${state} ${zip}`
  we will have the next result: '1408 W Front St  , Roanoke, il 651561'.
  If we will use next code:
    {combineStrings([
      `${combineStrings([
        address1,
        address2,
      ])},`,
      `${city},`,
      state,
      zip,
      ])
    }
  the result will be next: '1408 W Front St, Roanoke, il 651561'.
  As we can see the last result doesn't contain two spaces before the comma.
  The same method will removed one and more space from the start and end of the string.
*/
export const combineStrings = (args: Array<string | number>) => {
  if (!args.length) return '';
  return args
    .join(' ')
    .replace(/\s{2,}/, ' ')
    .replace(/^\s+|\s+$/, '');
};

export const camelize = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_m: string, chr: string) =>
      chr.toUpperCase()
    );
};

export const toTitleCase = (value: string): string => {
  return value
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const snakeToTitleCase = (value: string): string => {
  return toTitleCase(value.replace(/_/g, ' '));
};

/**
 * Converts the "?key=value" from a URL into a plain object
 * like { key: 'value' }
 *
 * @param search search string from a URL
 */
export const paramsFromSearchString = (
  search: string
): { [key: string]: string } => {
  return search
    .replace(/^\?/, '') // ditch the opening '?'
    .split('&') // break the key value pairs into strings
    .reduce((acc, val) => {
      // break the key value pairs into an array [ key, value ]
      const pair = val.split('=');
      // combine them back into a usable object
      return {
        ...acc,
        [pair[0]]: pair[1],
      };
    }, {});
};

export const stripLeadingZeros = (value?: string) =>
  value ? value.replace(/^[0]+/g, '') : value;

export const EMDASH = '—';

export const stringToTestId = (str: string) =>
  str.replace(/\s|\.|_/g, '-').toLowerCase();

/**
 * Returns the given string if it is not empty or undefined, otherwise EMDASH
 * @param value the value to potentially default to EMDASH
 * @returns either the given value or EMDASH if it was undefined or empty
 */
export const valueOrEmdash = (value?: string) =>
  value !== '' ? value ?? EMDASH : EMDASH;

export const booleanValueMapping: {
  [key: string]: string;
} = {
  true: 'Yes',
  false: 'No',
};
export const padStartWith = (
  originalString: string,
  size: number,
  padChar: string = '0'
): string => {
  let s = originalString + '';
  while (s.length < size) s = `${padChar}${s}`;
  return s;
};

export const stringToBoolean = (value: string) => /true/i.test(value);

export const formatText = (
  value: string,
  format: 'lowercase' | 'capitalize' | 'uppercase'
): string => {
  switch (format) {
    case 'lowercase':
      return value.toLowerCase();
    case 'uppercase':
      return value.toUpperCase();
    case 'capitalize':
    default:
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
};

export const toSnakeCase = (value: string): string => {
  return value.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
};

export const toCamelCase = <T extends string>(value: string): T => {
  return value.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()) as T;
};
